import authFetch from './authFetch';
import getConfig from 'next/config';

const {
  publicRuntimeConfig: { ACCOUNTS_SERVICE_HOST },
} = getConfig();

const ESPARK_REFERRER = 'student.esparklearning.com';
const GOOGLE_CLASSROOM_REFERRER = 'classroom.google.com';

async function request(url, options) {
  const response = await authFetch(url, {
    cache: 'default',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });
  if (!response.ok) {
    throw await response.json();
  }

  if (response.status === 204) {
    return;
  }

  const result = await response.json();
  if (result?.status === false) {
    throw new Error(result.message);
  }

  return result;
}

/**
 * Makes a request passing the cookies from the request (req) to url. This is useful for serverSide requests that
 * require authentication.
 *
 * @param req NextApiRequest
 * @param url Full URL -- must include the host
 * @param options Request options
 * @returns {Promise<any>}
 */
async function serverSideRequest(req, url, options) {
  const response = await authFetch(url, {
    cache: 'default',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Cookie: req.headers.cookie,
    },
    ...options,
  });
  if (!response.ok) {
    throw await response.json();
  }

  if (response.status === 204) {
    return;
  }

  const result = await response.json();
  if (result?.status === false) {
    throw new Error(result.message);
  }
  return result;
}

export default {
  checkCanDownload: async function(
    contentType,
    contentId,
    serverSideReq = undefined
  ) {
    let url = `/?__call=Auth&action=canDownload&type=${contentType}&id=${contentId}`;
    const options = {
      method: 'POST',
    };

    if (serverSideReq) {
      return await serverSideRequest(
        serverSideReq,
        `https://${serverSideReq.headers.host}${url}`,
        options
      );
    }
    return await request(url, options);
  },
  getMemberCredits: async function(memberId) {
    return await request(
      ACCOUNTS_SERVICE_HOST + `/members/${memberId}/credits`,
      {
        method: 'POST',
        mode: 'cors',
        cache: 'default',
        credentials: 'include',
      }
    );
  },
  getESparkToken: async function(memberId, contentId) {
    return await request(
      ACCOUNTS_SERVICE_HOST + `/members/${memberId}/espark/${contentId}`,
      {
        method: 'GET',
        mode: 'cors',
        credentials: 'same-origin',
      }
    );
  },
  getGoogleClassroomSecret: async function(memberId, contentId) {
    return await request(
      ACCOUNTS_SERVICE_HOST +
        `/members/${memberId}/googleclassroom/${contentId}`,
      {
        method: 'GET',
        mode: 'cors',
        credentials: 'same-origin',
      }
    );
  },
  fetchSinglePlayToken: async function(referrer = null, memberId, contentId) {
    try {
      switch (referrer) {
        case ESPARK_REFERRER:
          return await this.getESparkToken(memberId, contentId);
        case GOOGLE_CLASSROOM_REFERRER:
          return await this.getGoogleClassroomSecret(memberId, contentId);
        default:
          return null;
      }
    } catch (e) {
      console.log(e);
    }
  },
  recordGoogleClassroomShare: async function(contentType, contentId) {
    let data = {
      contentId: contentId,
      contentType: contentType,
    };
    return request('/api/member/record-gc-share', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  createInteractiveWorksheetDirectShare: async function(worksheetId) {
    // Get the user's timezone identifier (e.g. America/Chicago)
    const timezoneIdentifier = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return request('/api/interactive-worksheet/create-direct-share', {
      method: 'POST',
      body: JSON.stringify({
        worksheetId: worksheetId,
        timezone: timezoneIdentifier,
      }),
    });
  },
  userInfoFromToken: async function(token, serverSideReq = undefined) {
    let url = `/api/interactive-worksheet/get-user-info-from-token/${token}`;
    const options = {
      method: 'GET',
    };

    if (serverSideReq) {
      return await serverSideRequest(
        serverSideReq,
        `https://${serverSideReq.headers.host}${url}`,
        options
      );
    }
    return await request(url, options);
  },
};
